import { FiArrowUpRight, FiCalendar, FiClock, FiMapPin, FiPackage, FiShield, FiTag } from "react-icons/fi";
import PrimitiveAction from "../common/PrimitiveAction.jsx";
import StatusBanner from "../StatusBanner/StatusBanner.jsx";
import { siteContent } from "../../data/siteContent.js";
import eventBadge from "../../assets/generated/event-badge.webp";
import fingerboardProduct from "../../assets/generated/fingerboard-product.webp";
import hardwareDetail from "../../assets/generated/hardware-detail.webp";
import heroSceneBackground from "../../assets/generated/hero-scene-background.webp";
import transparentLogo from "../../assets/generated/magdump-logo-transparent.webp";
import materialTexture from "../../assets/generated/material-texture.webp";

const productVisuals = [
  { src: fingerboardProduct, className: "product-card--deck" },
  { src: hardwareDetail, className: "product-card--parts" },
  { src: eventBadge, className: "product-card--bundle" },
  { src: materialTexture, className: "product-card--gear" },
];

function buildCountdownItems(targetIso) {
  const target = new Date(targetIso).getTime();
  const remaining = Math.max(0, target - Date.now());
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  return [
    { label: "Days", value: String(Math.floor(remaining / day)) },
    { label: "Hours", value: String(Math.floor((remaining % day) / hour)).padStart(2, "0") },
    { label: "Minutes", value: String(Math.floor((remaining % hour) / minute)).padStart(2, "0") },
  ];
}

export function Hero() {
  return (
    <section className="hero">
      <img className="hero-backdrop" src={heroSceneBackground} alt="" aria-hidden="true" draggable="false" />
      <div className="hero-copy">
        <p className="eyebrow">{siteContent.brand.kicker}</p>
        <h1 className="hero-accessible-title">{siteContent.brand.name}</h1>
        <img className="hero-wordmark" src={transparentLogo} alt="" aria-hidden="true" draggable="false" />
        <div className="hero-rule" aria-hidden="true" />
        <p className="hero-lede">{siteContent.brand.headline}</p>
        <p className="hero-body">{siteContent.brand.intro}</p>
        <div className="action-row">
          <PrimitiveAction to="/contact" variant="primary" icon={FiArrowUpRight}>
            {siteContent.brand.primaryAction}
          </PrimitiveAction>
          <PrimitiveAction to="/event" variant="secondary" icon={FiCalendar}>
            {siteContent.brand.secondaryAction}
          </PrimitiveAction>
        </div>
      </div>
    </section>
  );
}

export function LaunchStrip() {
  return (
    <section className="launch-bridge" aria-label="Launch snapshot">
      <div className="bridge-copy">
        <p className="eyebrow">Drop 01</p>
        <h2>First boards, hard parts, and event access in one opening drop.</h2>
        <StatusBanner
          status={siteContent.launchStatus.status}
          customMessage={siteContent.launchStatus.message}
          className="magdump-launch-status"
        />
        <p>
          Join the list for board timing, rail and ledge notes, event details, and the first
          purchase window when Drop 01 is ready.
        </p>
      </div>
      <div className="signal-grid">
        {siteContent.launchStats.map((item) => (
          <div className="stat" key={item.label}>
            <strong>{item.value}</strong>
            <span>{item.label}</span>
            <p>{item.detail}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function HardwareSection({ full = false }) {
  return (
    <section className="section hardware-section">
      <div className="section-intro">
        <p className="eyebrow">Hardware</p>
        <h2>Compact decks, rails, and hard parts for the first run.</h2>
        <p>
          A tight first lineup keeps the focus on finish, texture, and pieces riders can picture
          on the table before a session.
        </p>
      </div>
      <div className="product-grid">
        {siteContent.shopPreview.map((item, index) => (
          <article className={`product-card ${productVisuals[index].className}`} key={item.title}>
            <div className="product-visual">
              <img src={productVisuals[index].src} alt={item.visualAlt} draggable="false" />
            </div>
            <div className="product-body">
              <div className="card-topline">
                <span>{item.status}</span>
                <span>{item.demoAvailability}</span>
              </div>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
              <div className="product-meta">
                <span>
                  <FiPackage aria-hidden="true" />
                  {item.demoSpec}
                </span>
                <strong>{item.demoPrice}</strong>
              </div>
              <span className="status-chip">
                <FiShield aria-hidden="true" />
                Drop list open
              </span>
            </div>
          </article>
        ))}
      </div>
      {full ? (
        <div className="route-note">
          <h3>Drop access</h3>
          <p>
            The drop list opens first. Product links, sizing notes, and purchase timing follow
            when the first run is locked.
          </p>
        </div>
      ) : null}
    </section>
  );
}

export function EventSection({ full = false }) {
  const countdownItems = buildCountdownItems(siteContent.event.demoDateIso);
  return (
    <section className="section event-section">
      <div className="event-copy">
        <p className="eyebrow">Event</p>
        <h2>{siteContent.event.title}</h2>
        <p>{siteContent.event.copy}</p>
        <div className="event-facts">
          <span><FiCalendar aria-hidden="true" />{siteContent.event.date}</span>
          <span><FiMapPin aria-hidden="true" />{siteContent.event.venue}</span>
          <span><FiClock aria-hidden="true" />{siteContent.event.dateStatus}</span>
        </div>
      </div>
      <div className="event-panel">
        <img
          className="event-badge"
          src={eventBadge}
          alt="Magdump event badge visual with fingerboard obstacles"
          draggable="false"
        />
        <div className="event-panel-content">
          <div className="countdown-shell" aria-label="Event countdown">
            {countdownItems.map((item) => (
              <div key={item.label}>
                <strong>{item.value}</strong>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
          <div className="raffle-block">
            <h3>Raffle Included</h3>
            <p>{siteContent.event.raffle}</p>
          </div>
          <p className="ticket-note">{siteContent.event.ticketIntent}</p>
        </div>
      </div>
      {full ? <EventDetails /> : null}
    </section>
  );
}

function EventDetails() {
  return (
    <div className="event-demo-details">
      <div className="schedule-card">
        <p className="eyebrow">Run Of Show</p>
        <h3>Event Schedule</h3>
        <div className="timeline-list">
          {siteContent.event.schedule.map((item) => (
            <article key={item.time}>
              <time>{item.time}</time>
              <div>
                <h4>{item.title}</h4>
                <p>{item.detail}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
      <div className="ticket-grid">
        {siteContent.event.ticketTiers.map((item) => (
          <article className="ticket-card" key={item.title}>
            <FiTag aria-hidden="true" />
            <div>
              <span>{item.price}</span>
              <h4>{item.title}</h4>
              <p>{item.detail}</p>
            </div>
          </article>
        ))}
      </div>
      <div className="route-note event-route-note">
        <h3>Ticket access</h3>
        <p>{siteContent.event.venueNote}</p>
      </div>
    </div>
  );
}
